import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import QRCode from 'qrcode'
import { CheckCircle2, KeyRound, Shield, ShieldCheck } from 'lucide-react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import { useChangeOwnPassword } from '@/features/admin/passwordApi'
import { api } from '@/lib/api/client'
import { useAuthStore } from '@/lib/auth/store'

interface StartRequest {
  password: string
  existingCode?: string
}

interface StartResponse {
  secret: string
  otpauthUri: string
}

interface ConfirmRequest {
  password: string
  code: string
}

interface ConfirmResponse {
  ok: boolean
  error?: string | null
}

interface StatusResponse {
  enrolled: boolean
}

/**
 * Self-service TOTP enrolment at /profile/security.
 *
 * <p>The flow is gated by a password step-up (W11.1): the user re-types
 * their account password to start enrolment, and — when rebinding an
 * already-enrolled secret — also a code from the CURRENT authenticator.
 * After scanning the QR they confirm with a code from the NEW
 * authenticator. The password is held in component state across the
 * start→confirm transition and dropped on success; it is never persisted.</p>
 */
export function SecurityPage() {
  const qc = useQueryClient()
  const status = useQuery({
    queryKey: ['totp', 'status'],
    queryFn: async () => (await api.get<StatusResponse>('/v1/auth/totp/status')).data,
  })

  // Held across the start → confirm round-trip so the user only types the
  // password once. Cleared on confirm-success and never written to storage.
  const [password, setPassword] = useState('')
  const [existingCode, setExistingCode] = useState('')

  const start = useMutation({
    mutationFn: async (body: StartRequest) =>
      (await api.post<StartResponse>('/v1/auth/totp/start', body)).data,
  })

  const confirm = useMutation({
    mutationFn: async (body: ConfirmRequest) => {
      const res = await api.post<ConfirmResponse>('/v1/auth/totp/confirm', body)
      return res.data
    },
    onSuccess: (data) => {
      if (data.ok) {
        qc.invalidateQueries({ queryKey: ['totp', 'status'] })
        setPassword('')
        setExistingCode('')
      }
    },
  })

  function resetForRebind() {
    start.reset()
    confirm.reset()
  }

  const enrolled = status.data?.enrolled ?? false
  const showStartForm = status.data && !start.isSuccess && !(confirm.isSuccess && confirm.data?.ok)
  const showQrPanel = start.isSuccess && !(confirm.isSuccess && confirm.data?.ok)

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Profile
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <Shield className="h-5 w-5" /> Security
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Time-based one-time passwords (TOTP) add a second factor for
          web logins. Required for manager roles in production.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Two-factor authentication</CardTitle>
              <CardDescription>
                Pair an authenticator app (Google Authenticator, Authy, 1Password)
                with your Graviton account.
              </CardDescription>
            </div>
            {status.data && (
              <Badge tone={enrolled ? 'positive' : 'slate'}>
                {enrolled ? 'ENROLLED' : 'NOT ENROLLED'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.isPending && <Skeleton className="h-20 w-full" />}
          {status.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn't read TOTP status</AlertTitle>
              <AlertDescription>Sign out and back in if this persists.</AlertDescription>
            </Alert>
          )}

          {showStartForm && enrolled && (
            <Alert variant="positive">
              <ShieldCheck className="h-4 w-4" />
              <AlertTitle>TOTP is enrolled</AlertTitle>
              <AlertDescription>
                Future logins will ask for a 6-digit code from your
                authenticator app. To rotate your secret (e.g. you lost
                your device), re-enrol below — the existing secret stays
                active until you confirm the new code.
              </AlertDescription>
            </Alert>
          )}

          {showStartForm && (
            <StartPanel
              enrolled={enrolled}
              password={password}
              existingCode={existingCode}
              isPending={start.isPending}
              error={startError(start.error)}
              onPasswordChange={(v) => {
                setPassword(v)
                if (start.isError) start.reset()
              }}
              onExistingCodeChange={(v) => {
                setExistingCode(v)
                if (start.isError) start.reset()
              }}
              onSubmit={() => {
                const body: StartRequest = { password }
                if (enrolled) body.existingCode = existingCode
                start.mutate(body)
              }}
            />
          )}

          {showQrPanel && start.data && (
            <EnrolPanel
              secret={start.data.secret}
              otpauthUri={start.data.otpauthUri}
              isPending={confirm.isPending}
              error={confirmError(confirm)}
              onSubmit={(code) => confirm.mutate({ password, code })}
              onStartOver={() => {
                resetForRebind()
              }}
            />
          )}

          {confirm.isSuccess && confirm.data?.ok && (
            <Alert variant="positive">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>TOTP enrolled</AlertTitle>
              <AlertDescription>
                Your authenticator app is paired. The next time you sign in
                on this tenant you'll be asked for a 6-digit code.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <ChangePasswordCard />
    </div>
  )
}

/**
 * Self-service password change. Sits alongside the TOTP card so /profile/security
 * is the single Settings → Security surface. Successful change revokes every
 * live session; we log out locally and bounce to /login.
 */
function ChangePasswordCard() {
  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)
  const change = useChangeOwnPassword()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNew, setConfirmNew] = useState('')

  const tooShort = newPassword.length > 0 && newPassword.length < 8
  const mismatch = confirmNew.length > 0 && confirmNew !== newPassword
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 8 && confirmNew === newPassword

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          // Sessions revoked server-side; old access token will 401 on
          // the next call. Get ahead of the redirect storm.
          logout('user_initiated')
          navigate({ to: '/login' })
        },
      },
    )
  }

  const errorMessage = (() => {
    if (!change.isError) return null
    if (!isAxiosError(change.error)) return 'Could not change password.'
    const detail = change.error.response?.data?.detail
    if (typeof detail === 'string') {
      if (detail.includes('current password')) return 'Your current password is incorrect.'
      if (detail.includes('at least 8')) return 'New password must be at least 8 characters.'
      return detail
    }
    return 'Could not change password.'
  })()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
        <CardDescription>
          You'll be signed out of all sessions when you save.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
            {tooShort && (
              <p className="mt-1 text-2xs text-negative">At least 8 characters.</p>
            )}
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmNew}
              onChange={(e) => setConfirmNew(e.target.value)}
              required
            />
            {mismatch && (
              <p className="mt-1 text-2xs text-negative">Doesn't match.</p>
            )}
          </div>
          {errorMessage && (
            <Alert variant="destructive">
              <AlertTitle>Couldn't change password</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={!canSubmit || change.isPending}>
            <KeyRound className="h-3.5 w-3.5" />
            {change.isPending ? 'Updating…' : 'Change password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function StartPanel({
  enrolled,
  password,
  existingCode,
  isPending,
  error,
  onPasswordChange,
  onExistingCodeChange,
  onSubmit,
}: {
  enrolled: boolean
  password: string
  existingCode: string
  isPending: boolean
  error: string | null
  onPasswordChange: (v: string) => void
  onExistingCodeChange: (v: string) => void
  onSubmit: () => void
}) {
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!password) return
    if (enrolled && existingCode.length !== 6) return
    onSubmit()
  }

  const canSubmit = password.length > 0 && (!enrolled || existingCode.length === 6)

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <Label htmlFor="totp-password">Current password</Label>
        <Input
          id="totp-password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>
      {enrolled && (
        <div>
          <Label htmlFor="totp-existing">
            Current 6-digit code
          </Label>
          <Input
            id="totp-existing"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={existingCode}
            onChange={(e) => onExistingCodeChange(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="font-mono tracking-[0.4em]"
            autoComplete="one-time-code"
          />
          <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
            From the authenticator you're already using.
          </p>
        </div>
      )}
      <Button type="submit" disabled={!canSubmit || isPending}>
        <KeyRound className="h-3.5 w-3.5" />
        {isPending ? 'Verifying…' : enrolled ? 'Rotate / re-enrol' : 'Enrol TOTP'}
      </Button>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't start enrolment</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </form>
  )
}

function EnrolPanel({
  secret,
  otpauthUri,
  isPending,
  error,
  onSubmit,
  onStartOver,
}: {
  secret: string
  otpauthUri: string
  isPending: boolean
  error: string | null
  onSubmit: (code: string) => void
  onStartOver: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [code, setCode] = useState('')

  // Render the QR code to a canvas once we have the URI. Done client-side so
  // we don't have to push a PNG over the wire.
  useEffect(() => {
    if (!canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, otpauthUri, {
      width: 200,
      margin: 1,
      color: { dark: '#0E2138', light: '#FFFFFF' },
    }).catch(() => { /* swallow — UI degrades to the secret string */ })
  }, [otpauthUri])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (code.length === 6) onSubmit(code)
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-[200px_1fr] sm:items-start">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <canvas ref={canvasRef} className="block" aria-label="TOTP enrolment QR code" />
        </div>
        <div className="space-y-3">
          <div>
            <Label>Or type the secret manually</Label>
            <Input value={secret} readOnly className="font-mono text-xs" />
            <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
              Use this if your authenticator can't scan QR codes.
            </p>
          </div>
          <Alert variant="warning">
            <AlertTitle>Don't close this page yet</AlertTitle>
            <AlertDescription>
              The pairing isn't active until you confirm a code below.
            </AlertDescription>
          </Alert>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="totp">Confirmation code</Label>
          <Input
            id="totp"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="font-mono tracking-[0.4em]"
            autoFocus
          />
        </div>
        <Button type="submit" disabled={code.length !== 6 || isPending}>
          {isPending ? 'Confirming…' : 'Confirm pairing'}
        </Button>
      </form>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Code rejected</AlertTitle>
          <AlertDescription>
            {error}
            {error === PASSWORD_ERROR_MESSAGES.password_invalid && (
              <>
                {' '}
                <button
                  type="button"
                  className="underline"
                  onClick={onStartOver}
                >
                  Re-enter password
                </button>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

const PASSWORD_ERROR_MESSAGES = {
  password_required: 'Enter your current account password.',
  password_invalid: 'Password incorrect',
  existing_totp_required: 'Enter a code from your current authenticator',
  existing_totp_invalid: "Code from current authenticator didn't match",
  code_invalid: 'Code incorrect. Try again — your phone clock may need to sync.',
} as const

function apiErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null
  const data = err.response?.data
  if (data && typeof data === 'object' && 'error' in data) {
    const code = (data as { error: unknown }).error
    return typeof code === 'string' ? code : null
  }
  return null
}

function startError(err: unknown): string | null {
  if (!err) return null
  const code = apiErrorCode(err)
  if (code && code in PASSWORD_ERROR_MESSAGES) {
    return PASSWORD_ERROR_MESSAGES[code as keyof typeof PASSWORD_ERROR_MESSAGES]
  }
  return 'Could not start enrolment. Try again.'
}

function confirmError(confirm: {
  isError: boolean
  error: unknown
  data: ConfirmResponse | undefined
}): string | null {
  if (confirm.isError) {
    const code = apiErrorCode(confirm.error)
    if (code && code in PASSWORD_ERROR_MESSAGES) {
      return PASSWORD_ERROR_MESSAGES[code as keyof typeof PASSWORD_ERROR_MESSAGES]
    }
    return 'Could not confirm. Try again.'
  }
  if (confirm.data && confirm.data.ok === false) {
    const code = confirm.data.error
    if (code && code in PASSWORD_ERROR_MESSAGES) {
      return PASSWORD_ERROR_MESSAGES[code as keyof typeof PASSWORD_ERROR_MESSAGES]
    }
    return code ?? 'Code incorrect.'
  }
  return null
}
